import { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import { ChevronRight, Home } from 'lucide-react';
import IconImages from '@shared/assets/images/icons';
import { PUBLICATION_TYPE } from '@shared/types/enums';
import { calculateTaxIncludedPrice } from '@shared/utils/utils';
import { formatDateTime } from '@shared/utils/datetime';
import { DATE_YMD } from '@shared/components/constants/common.constants';
import { useStore } from '@shared/hooks/useStore';
import { toastify } from '@shared/utils/toastify';
import { flowResult } from 'mobx';
import { ROUTES } from '@shared/configs/constants';
import defaultCover from '@shared/assets/images/default-cover.png';
import { formatPublicationTitle } from '@shared/utils/publication';
import { showConfirmReadModal } from '@user/components/publication/ConfirmReadModal';
import NotFoundPage from '@/shared/components/common/NotFoundPage';

const PublicationDetailPage = observer(() => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { authStore, publicationStore, apiStore, cartStore, modalStore, readerStore } = useStore();
    const { isLoggedIn, profile, isStdIndividual } = authStore;
    const { publicationDetail: publication } = publicationStore;
    const isLoading = apiStore.isLoading;
    const publicationTitle = publication ? formatPublicationTitle(publication) : '';

    useEffect(() => {
        if (!id) return;
        publicationStore.fetchPublicationDetail(id);

        return () => {
            publicationStore.setObservable('publicationDetail', null, {});
        };
    }, [id, publicationStore]);

    const formatPrice = (price: number) => {
        return new Intl.NumberFormat('ja-JP').format(price);
    };

    const shouldShowPurchaseButtons = () => {
        return !isLoggedIn || (isStdIndividual && !(publication?.owned || publication?.isPurchase));
    };

    const shouldShowPrice = () => {
        return !isLoggedIn || isStdIndividual;
    };

    const shouldShowPreviewButton = () => {
        if (publication && publication.owned) return false;
        return true;
    };

    const shouldShowReadAllButton = () => {
        if (publication && publication.owned) return true;

        const pkg = profile?.package;
        const packageSelected = profile?.packageSelected;
        if (pkg && new Date(pkg.startDate) <= new Date() && new Date(pkg.expirationDate) > new Date() && packageSelected?.remainSelected && packageSelected.remainSelected > 0) {
            return true;
        }
        return false;
    };

    const handleBuyNow = async () => {
        if (!isLoggedIn) {
            modalStore.showLoginModal();
            return;
        }
        if (!publication) {
            toastify('error', '商品情報が取得できませんでした');
            return;
        }

        const res = await flowResult(cartStore.getPreCheckoutCart([publication.id.toString()]));
        if (res) {
            navigate(ROUTES.checkout.href, { state: { cartItems: res } });
        }
    };

    const handleAddToCart = async () => {
        if (!isLoggedIn) {
            modalStore.showLoginModal();
            return;
        }
        if (!publication) {
            toastify('error', '商品情報が取得できませんでした');
            return;
        }

        await flowResult(cartStore.addToCart(Number(publication.id)));
    };

    const handlePreview = () => {
        if (!isLoggedIn) {
            modalStore.showLoginModal();
            return;
        }
        if (publication) {
            readerStore.recordRecentlyViewed(publication.id);
            window.open(`/preview/${publication.id}`, 'jiho_preview');
        }
    };

    const handleReadAll = () => {
        if (!publication) return;

        const readAll = (externalWindow?: Window | null) => {
            readerStore.recordRecentlyViewed(publication.id);
            readerStore.recordReading(publication.id);
            const url = `/reader/${publication.id}`;
            if (externalWindow) {
                externalWindow.location.href = url;
            } else {
                window.open(url, 'jiho_reader');
            }
        };

        if (publication.owned) {
            readAll();
            return;
        }

        showConfirmReadModal(modalStore, async () => {
            const newWindow = window.open('about:blank', 'jiho_reader');
            try {
                await flowResult(publicationStore.selectPublication(publication.id));
                await flowResult(authStore.getProfile());
                await flowResult(publicationStore.fetchPublicationDetail(publication.id.toString()));
                readAll(newWindow);
            } catch (error) {
                newWindow?.close();
            }
        });
    };

    if (isLoading && !publication) {
        return (<></>);
    }

    if (!publication) {
        return (
            <NotFoundPage homeHref={ROUTES.home.href} />
        );
    }

    return (
        <div className='container mx-auto px-4 py-6 md:py-12 space-y-12 text-break'>
            {/* Breadcrumb */}
            <nav className='hidden md:flex items-center gap-2 text-[11px] font-medium text-[#404040]'>
                <button
                    onClick={() => navigate('/')}
                    className='flex items-center gap-1 hover:text-primary transition-colors flex-shrink-0'
                >
                    <Home size={14} />
                    ホーム
                </button>
                <ChevronRight size={14} className='text-[#404040]' />
                <button
                    onClick={() => navigate(publication.type === PUBLICATION_TYPE.MagazineIssue ? '/magazines' : '/books')}
                    className='hover:text-primary transition-colors flex-shrink-0'
                >
                    {publication.type === PUBLICATION_TYPE.MagazineIssue ? '雑誌' : '書籍'}
                </button>
                <ChevronRight size={14} className='text-[#404040]' />
                <span className='line-clamp-1'>
                    {publicationTitle.length > 50 ? publicationTitle.slice(0, 50) + '...' : publicationTitle}
                </span>
            </nav>

            {/* Main Content */}
            <div>
                <div className='flex flex-col lg:flex-row gap-6 md:gap-8'>
                    {/* Left: Publication Image */}
                    <div className='mx-auto flex-shrink-0 w-[327px] xl:w-[423px]'>
                        <img
                            src={publication.linkImg}
                            alt={publication.title}
                            className='w-full aspect-[3/4] object-fill border border-white shadow-sm'
                            onError={(e) => {
                                e.currentTarget.src = defaultCover;
                            }}
                        />
                    </div>

                    {/* Center: Publication Info */}
                    <div className='flex-1'>
                        {/* Title */}
                        <h1 className='text-[29px] font-semibold leading-[36px] mb-3'>
                            {publicationTitle}
                        </h1>

                        {/* Author / Metadata Source */}
                        {publication.type === PUBLICATION_TYPE.Book &&
                            (
                                <p className='font-normal leading-[29px] mb-6'>
                                    著者：
                                    {publication.author || '不明'}
                                </p>
                            )}

                        {/* Meta Info */}
                        <div className='mb-6'>
                            {publication.type === PUBLICATION_TYPE.MagazineIssue ?
                                (
                                    <div className='grid grid-cols-2 md:grid-cols-3 gap-2 leading-[26px]'>
                                        <div className='flex flex-col'>
                                            <span className='text-[#999999]'>雑誌名</span>
                                            <span>{publication.magazineTitle || '不明'}</span>
                                        </div>
                                        <div className='flex flex-col'>
                                            <span className='text-[#999999]'>特集</span>
                                            <span>{publication.title}</span>
                                        </div>
                                        <div className='flex flex-col'>
                                            <span className='text-[#999999]'>カテゴリ</span>
                                            <span>{publication.categories?.map(c => c.label).join('、')}</span>
                                        </div>
                                        <div className='flex flex-col'>
                                            <span className='text-[#999999]'>発行日</span>
                                            <span>{formatDateTime(publication.releaseDate, DATE_YMD, { returnDefault: '未定' })}</span>
                                        </div>
                                        <div className='flex flex-col'>
                                            <span className='text-[#999999]'>巻</span>
                                            <span>{publication.seriesName || ''}</span>
                                        </div>
                                        <div className='flex flex-col'>
                                            <span className='text-[#999999]'>号</span>
                                            <span>{publication.issueNumber || ''}</span>
                                        </div>
                                    </div>
                                ) :
                                (
                                    <div className='leading-[29px]'>
                                        <div className='flex'>
                                            <span className='text-[#999999] w-24 flex-shrink-0'>カテゴリ</span>
                                            <span className='flex-1'>
                                                {publication.categories?.map(c => c.label).join('、')}
                                            </span>
                                        </div>
                                        <div className='flex'>
                                            <span className='text-[#999999] w-24'>発行日</span>
                                            <span>
                                                {formatDateTime(publication.releaseDate, DATE_YMD, { returnDefault: '未定' })}
                                            </span>
                                        </div>
                                    </div>

                                )}
                        </div>

                        {shouldShowPrice() && (
                            <div className='flex items-baseline'>
                                <span className='text-[22px] font-bold text-danger'>
                                    ¥
                                    {formatPrice(calculateTaxIncludedPrice(publication.price))}
                                </span>
                                <span className='text-xs leading-[22px]'>（税込）</span>
                            </div>
                        )}

                        <div className='hidden md:block'>
                            <div className='h-[1px] w-full bg-[#D9D9D9] my-6' />
                            <div className='prose prose-sm max-w-none'>
                                <p className='leading-[29px] whitespace-pre-line'>
                                    {publication.description}
                                </p>
                            </div>
                        </div>
                    </div>

                    <div className='w-full lg:w-[306px] flex-shrink-0'>
                        <div className='lg:sticky lg:top-[136px] space-y-3'>
                            {shouldShowPurchaseButtons() && (
                                <>
                                    <button
                                        onClick={handleBuyNow}
                                        className='w-full flex items-center justify-center gap-1 px-6 py-4 bg-primary text-white font-semibold rounded-[10px] hover:bg-primary-hover'
                                    >
                                        <img src={IconImages.booksIc} alt='books' />
                                        今すぐ買う
                                    </button>
                                    <button
                                        onClick={handleAddToCart}
                                        className='w-full flex items-center justify-center gap-1 px-6 py-4 bg-primary-light text-white font-semibold rounded-[10px] hover:bg-primary-light-hover'
                                    >
                                        <img src={IconImages.shoppingCartIc} alt='shopping-cart' />
                                        カートへ追加
                                    </button>
                                </>
                            )}
                            {shouldShowPreviewButton() && (
                                <button
                                    onClick={handlePreview}
                                    className='w-full flex items-center justify-center gap-1 px-6 py-4 border border-primary-light text-primary-light font-semibold rounded-[10px]'
                                >
                                    <img src={IconImages.eyeIc} alt='eye' className='-mb-[2px]' />
                                    試し読み
                                </button>
                            )}
                            {shouldShowReadAllButton() && (
                                <button
                                    onClick={handleReadAll}
                                    className='w-full flex items-center justify-center gap-1 px-6 py-4 border border-primary-light text-primary-light font-semibold rounded-[10px]'
                                >
                                    <img src={IconImages.bookOpenIc} alt='book-open' />
                                    今すぐ読む
                                </button>
                            )}
                        </div>
                    </div>

                    <div className='block md:hidden'>
                        <div className='h-[1px] w-full bg-[#D9D9D9] mb-6' />
                        <div className='prose prose-sm max-w-none'>
                            <p className='leading-[29px] whitespace-pre-line'>
                                {publication.description}
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
});

export default PublicationDetailPage;
